import { useState } from 'react';

import { useAppSelector } from 'store';
import { List } from 'components/list';
import { Popup } from 'components/popup';
import { Dialog } from 'components/dialog';
import { formatPrice } from 'utils/helpers';
import { Roles, BackOfficeRoles } from 'types';
import { Button, ButtonWithRef } from 'components/button';
import { hasUserRole, selectCanReadMissingSales } from 'store/slices/auth';
import {
  Event,
  useMissingSalesQuery,
  useMissingSaleHistoryQuery,
} from 'store/api/event';

import * as S from './styles';
import { MissingSaleForm } from './form';
import {
  SaleKind,
  MissingSale,
  totalAmount,
  tenderLabels,
  missingSaleDisplayTotals,
} from './types';
const titles = [
  'Date',
  'Tender',
  'Sales (pre-tax)',
  'Tax',
  'Tip',
  'Fee',
  'Total',
  'Source',
  'Note',
  '',
];
const { template } = S;
const History = ({ row, eventId }: { eventId: number; row: MissingSale }) => {
  const { data, isError, isLoading } = useMissingSaleHistoryQuery({
    eventId,
    rowId: row.id,
  });
  return (
    <S.History>
      {isLoading && <p>Loading history…</p>}
      {isError && <S.ErrorText>Unable to load history.</S.ErrorText>}
      {data?.map((entry) => (
        <div key={entry.id}>
          <p>
            <strong>{entry.action}</strong> ·{' '}
            {new Date(entry.createdAt).toLocaleString()} · User {entry.actorId}
          </p>
          <p>{entry.reason}</p>
          <p>
            {entry.fromJson
              ? Object.keys(entry.toJson)
                  .filter(
                    (key) =>
                      JSON.stringify(
                        entry.fromJson?.[key as keyof MissingSale]
                      ) !==
                      JSON.stringify(entry.toJson[key as keyof MissingSale])
                  )
                  .map(
                    (key) =>
                      `${key}: ${JSON.stringify(entry.fromJson?.[key as keyof MissingSale])} → ${JSON.stringify(entry.toJson[key as keyof MissingSale])}`
                  )
                  .join('; ')
              : 'Record created'}
          </p>
        </div>
      ))}
    </S.History>
  );
};
export const MissingSales = ({ event }: { event: Event }) => {
  const canRead = useAppSelector(selectCanReadMissingSales);
  const writer = useAppSelector(
    hasUserRole([Roles.COMPANY_OWNER, BackOfficeRoles.ADMINISTRATIVE])
  );
  const [historyRow, setHistoryRow] = useState<MissingSale>();
  const [detailsRow, setDetailsRow] = useState<MissingSale>();
  const {
    isError,
    refetch,
    data = [],
    isLoading,
  } = useMissingSalesQuery(event.id, { skip: !canRead });
  const [limit, setLimit] = useState(40);
  const [selected, setSelected] = useState<MissingSale>();
  const [original, setOriginal] = useState<MissingSale>();
  const [kind, setKind] = useState<SaleKind>('sale');
  const [mode, setMode] = useState<'add' | 'edit' | 'delete'>();
  const close = () => {
    setMode(undefined);
    setSelected(undefined);
    setOriginal(undefined);
    setKind('sale');
  };
  const open = (
    action: 'add' | 'edit' | 'delete',
    row?: MissingSale,
    child?: SaleKind
  ) => {
    setSelected(child ? undefined : row);
    setOriginal(child ? row : undefined);
    setKind(child || row?.kind || 'sale');
    setMode(action);
  };
  const sales = data.filter((row) => row.kind === 'sale');
  const sumField = (
    rows: MissingSale[],
    key: 'taxAmount' | 'tipAmount' | 'grossAmount' | 'customerFeeAmount'
  ) => missingSaleDisplayTotals(rows)[key];
  const collected = (rows: MissingSale[]) =>
    missingSaleDisplayTotals(rows).total;
  const ordered = sales.flatMap((sale) => [
    sale,
    ...data.filter((row) => row.originalMissingSaleId === sale.id),
  ]);
  const visible = ordered.slice(0, limit);
  if (!canRead) {
    return null;
  }
  return (
    <S.Container>
      <S.Note>
        Sales taken at the show but never rung up in the register. They count in
        the P&amp;L, cash in hand and sales tax. They never create commissions,
        payroll, tips paid through CLIX, receipts, stock or Clover activity.
        <p>
          Your AI agent prepares and loads the approved list. Use Add missing
          sale only for a single correction.
        </p>
      </S.Note>
      <S.Totals>
        <S.SummaryCard
          title="Sales (pre-tax)"
          value={formatPrice(sumField(data, 'grossAmount'))}
        />
        <S.SummaryCard
          title="Tax"
          value={formatPrice(sumField(data, 'taxAmount'))}
        />
        <S.SummaryCard
          title="Tips"
          value={formatPrice(sumField(data, 'tipAmount'))}
        />
        <S.SummaryCard
          title="Fees"
          value={formatPrice(sumField(data, 'customerFeeAmount'))}
        />
        <S.SummaryCard
          title="Total collected"
          value={formatPrice(collected(data))}
        />
      </S.Totals>
      {!!data.length && (
        <>
          <S.SectionText>
            Totals deduct refunds and lost chargebacks. Open and won chargebacks
            do not change totals.
          </S.SectionText>
          <S.TenderTableContainer>
            <h2>By tender</h2>
            <S.TenderTable aria-label="By tender">
              <colgroup>
                <col style={{ width: '160px' }} />
                {[0, 1, 2, 3, 4].map((index) => (
                  <col key={index} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {['Tender', 'Sales', 'Tax', 'Tips', 'Fee', 'Total'].map(
                    (title) => (
                      <th key={title} scope="col">
                        {title}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {Object.entries(tenderLabels)
                  .filter(([tender]) =>
                    data.some((row) => row.tender === tender)
                  )
                  .map(([tender, label]) => {
                    const rows = data.filter((row) => row.tender === tender);
                    return (
                      <tr key={tender}>
                        <th scope="row">{label}</th>
                        {(
                          [
                            'grossAmount',
                            'taxAmount',
                            'tipAmount',
                            'customerFeeAmount',
                          ] as const
                        ).map((key) => (
                          <td
                            key={key}
                            data-label={
                              {
                                taxAmount: 'Tax',
                                tipAmount: 'Tips',
                                grossAmount: 'Sales',
                                customerFeeAmount: 'Fee',
                              }[key]
                            }
                          >
                            {formatPrice(sumField(rows, key))}
                          </td>
                        ))}
                        <td data-label="Total">
                          {formatPrice(collected(rows))}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </S.TenderTable>
          </S.TenderTableContainer>
        </>
      )}
      {writer && (
        <S.Actions>
          <ButtonWithRef
            text="Add missing sale"
            onClick={() => open('add')}
            aria-label="Add missing sale"
          />
        </S.Actions>
      )}
      {!writer && (
        <p>
          Read only. The Company Owner and Administrative can change these
          records.
        </p>
      )}
      {isError ? (
        <S.ErrorText role="alert">
          Unable to load missing sales.{' '}
          <Button text="Try again" onClick={refetch} />
        </S.ErrorText>
      ) : (
        <List
          count={data.length}
          isLoading={isLoading}
          mainWrapperStyles={{ padding: 0 }}
          header={
            <S.RowHeader
              gap="16px"
              titles={titles}
              padding="8px 16px"
              template={template}
            />
          }
        >
          {visible.map((row) => (
            <div key={row.id}>
              <S.RowCard
                gap={16}
                headers={titles}
                template={template}
                padding="12px 16px"
                values={[
                  row.saleDate,
                  `${row.kind === 'sale' ? '' : row.kind === 'refund' ? '↳ Refund · ' : '↳ Chargeback · '}${tenderLabels[row.tender]}${row.kind === 'chargeback' ? ` (${row.chargebackOutcome})` : ''}`,
                  <S.Money>{formatPrice(Number(row.grossAmount))}</S.Money>,
                  <S.Money>{formatPrice(Number(row.taxAmount))}</S.Money>,
                  <S.Money>{formatPrice(Number(row.tipAmount))}</S.Money>,
                  <S.Money>
                    {formatPrice(Number(row.customerFeeAmount))}
                  </S.Money>,
                  <S.Money>{formatPrice(totalAmount(row))}</S.Money>,
                  <S.Source>
                    <S.Truncated title={row.sourceRef}>
                      {row.sourceRef}
                    </S.Truncated>
                    {row.origin === 'bulk' && (
                      <S.LoadedTag
                        title={`Loaded from the approved list (${row.approvalRef})`}
                      >
                        Approved list · {row.approvalRef}
                      </S.LoadedTag>
                    )}
                  </S.Source>,
                  <S.Truncated title={row.note || ''}>{row.note}</S.Truncated>,
                  <Popup
                    width="200px"
                    position={['bottom right', 'top right']}
                    trigger={
                      <S.MenuButton
                        type="button"
                        aria-label={`Actions for ${row.saleDate} ${tenderLabels[row.tender]}`}
                      >
                        ⋯
                      </S.MenuButton>
                    }
                  >
                    {(close) => (
                      <S.RowActions>
                        <Button
                          text="View details"
                          onClick={() => {
                            close();
                            setDetailsRow(row);
                          }}
                        />
                        <Button
                          text="History"
                          onClick={() => {
                            close();
                            setHistoryRow(row);
                          }}
                        />
                        {writer && (
                          <>
                            <Button
                              text="Edit"
                              onClick={() => {
                                close();
                                open('edit', row);
                              }}
                            />
                            <Button
                              text="Delete"
                              onClick={() => {
                                close();
                                open('delete', row);
                              }}
                            />
                            {row.kind === 'sale' && (
                              <>
                                <Button
                                  text="Add refund"
                                  onClick={() => {
                                    close();
                                    open('add', row, 'refund');
                                  }}
                                />
                                <Button
                                  text="Add chargeback"
                                  onClick={() => {
                                    close();
                                    open('add', row, 'chargeback');
                                  }}
                                />
                              </>
                            )}
                          </>
                        )}
                      </S.RowActions>
                    )}
                  </Popup>,
                ]}
              />
            </div>
          ))}
        </List>
      )}
      {data.length > limit && (
        <Button
          text="Show 40 more"
          onClick={() => setLimit((value) => value + 40)}
        />
      )}
      <Dialog
        width="800px"
        removeAutofocus
        open={!!detailsRow}
        title="Missing sale details"
        onClose={() => setDetailsRow(undefined)}
      >
        {() =>
          detailsRow ? (
            <>
              <S.Details>
                <dt>Source</dt>
                <dd>{detailsRow.sourceRef}</dd>
                <dt>Approval reference</dt>
                <dd>{detailsRow.approvalRef || '—'}</dd>
                <dt>Note</dt>
                <dd>{detailsRow.note || '—'}</dd>
              </S.Details>
              <S.Actions>
                <Button text="Close" onClick={() => setDetailsRow(undefined)} />
              </S.Actions>
            </>
          ) : null
        }
      </Dialog>
      <Dialog
        width="800px"
        removeAutofocus
        open={!!historyRow}
        title="Missing sale history"
        onClose={() => setHistoryRow(undefined)}
      >
        {() =>
          historyRow ? <History row={historyRow} eventId={event.id} /> : null
        }
      </Dialog>
      <Dialog
        open={!!mode}
        width="800px"
        onClose={close}
        removeAutofocus
        title={
          mode === 'delete'
            ? 'Delete missing sale'
            : mode === 'edit'
              ? 'Edit missing sale'
              : `Add ${kind === 'sale' ? 'missing sale' : kind}`
        }
      >
        {() =>
          mode ? (
            <MissingSaleForm
              kind={kind}
              event={event}
              row={selected}
              onClose={close}
              original={original}
              remove={mode === 'delete'}
              key={`${mode}-${selected?.id || original?.id || 0}-${kind}`}
              originalSaleDate={
                data.find((row) => row.id === selected?.originalMissingSaleId)
                  ?.saleDate
              }
              onDuplicate={(id) => {
                const existing = data.find((row) => row.id === id);
                if (existing) {
                  open('edit', existing);
                }
              }}
            />
          ) : null
        }
      </Dialog>
    </S.Container>
  );
};
